import type { CartItem } from '~/types/Cart';
import type {
  MulticardCombination,
  MulticardPrimaryGroup,
  MulticardPrimaryGroupProp,
  MulticardVariantDimension,
  MulticardVariantDimensionValue,
  OfferFull,
  OfferMedium,
  OfferSmall,
} from '~/types/Catalog';

export type MulticardSelectedValues = Record<string, string>;
export type MulticardSelectedSizeCharts = Record<string, number>;

export function isMulticardV2Offer(offer: Pick<OfferFull, 'version'> | null | undefined): boolean {
  return offer?.version === 'multicard_v2';
}

export function getSelectedMulticardOffer(offer: OfferFull, selectedOfferId: number | null = null): OfferSmall | null {
  if (!isMulticardV2Offer(offer)) {
    return null;
  }

  const activeOfferId = selectedOfferId ?? offer.selected_offer_id;

  return offer.offers.find((nestedOffer) => nestedOffer.id === activeOfferId) ?? null;
}

export function getMulticardOfferThumbnailUrl(offer: OfferFull, offerId: number | null): string | null {
  if (!offerId) {
    return null;
  }

  const nestedOffer = getSelectedMulticardOffer(offer, offerId);
  const image = nestedOffer?.offer_images.find((offerImage) => offerImage.is_main) ?? nestedOffer?.offer_images[0];

  return image?.conversions?.thumb_url_360 ?? image?.url ?? null;
}

export function getMulticardPrimaryThumbnailUrl(
  offer: OfferFull,
  offerId: number | null,
  groupThumbnailUrl: string | null,
): string | null {
  if (!offerId) {
    return groupThumbnailUrl;
  }

  const nestedOffer = getSelectedMulticardOffer(offer, offerId);

  if (!nestedOffer) {
    return groupThumbnailUrl;
  }

  return getMulticardOfferThumbnailUrl(offer, offerId);
}

export function getFavoriteOffer(
  offer: OfferFull | OfferMedium | CartItem['data'],
  selectedOfferId: number | null = null,
): OfferFull | OfferMedium | OfferSmall | CartItem['data'] | null {
  if (isMulticardV2Offer(offer)) {
    return getSelectedMulticardOffer(offer as OfferFull, selectedOfferId);
  }

  return Number.isInteger(offer.id) && offer.id > 0 ? offer : null;
}

export function getActiveDimensionValues(
  dimension: MulticardVariantDimension,
  selectedSizeCharts: MulticardSelectedSizeCharts = {},
): MulticardVariantDimensionValue[] {
  const selectedSizeChartId = selectedSizeCharts[dimension.property_vendor_id] ?? dimension.size_chart_id ?? null;

  if (!selectedSizeChartId || !dimension.size_charts?.length) {
    return dimension.values;
  }

  return dimension.size_charts.find((sizeChart) => sizeChart.id === selectedSizeChartId)?.values ?? dimension.values;
}

export function getActivePrimaryGroupLabel(
  group: MulticardPrimaryGroup,
  selectedSizeCharts: MulticardSelectedSizeCharts = {},
): string {
  const props = Object.values(group.props ?? {});
  const values = props
    .map((prop) => getActivePrimaryPropValue(prop, selectedSizeCharts))
    .filter((value): value is string => Boolean(value));

  return values.length ? values.join(', ') : group.label;
}

export function getActivePrimaryPropValue(
  prop: MulticardPrimaryGroupProp,
  selectedSizeCharts: MulticardSelectedSizeCharts = {},
): string | null {
  const selectedSizeChartId = prop.property_vendor_id
    ? (selectedSizeCharts[prop.property_vendor_id] ?? prop.size_chart_id ?? null)
    : (prop.size_chart_id ?? null);

  if (!selectedSizeChartId || !prop.size_charts?.length) {
    return prop.value;
  }

  return prop.size_charts.find((sizeChart) => sizeChart.id === selectedSizeChartId)?.value ?? prop.value;
}

export function getInitialMulticardSelection(offer: OfferFull, requestedOfferId: number | null = null) {
  const combinations = offer.combinations ?? [];
  const currentGroupId = offer.group_id ?? null;
  const preferredOfferId = requestedOfferId ?? offer.selected_offer_id;
  const variantPropertyVendorIds = (offer.variant_dimensions ?? []).map((dimension) => dimension.property_vendor_id);
  const selectedCombination =
    combinations.find((combination) => {
      return combination.offer_id === preferredOfferId && combination.group_id === currentGroupId;
    }) ??
    (requestedOfferId ? combinations.find((combination) => combination.offer_id === requestedOfferId) : null) ??
    getFirstInStockCombinationForGroup(combinations, currentGroupId) ??
    combinations.find((combination) => combination.offer_id === offer.selected_offer_id && combination.balance > 0) ??
    combinations.find((combination) => combination.balance > 0) ??
    null;
  const selectedGroupId = selectedCombination?.group_id ?? currentGroupId ?? offer.primary_groups?.[0]?.id ?? null;

  const primarySizeCharts = Object.values(offer.primary_groups?.[0]?.props ?? {})
    .filter((prop) => prop.property_vendor_id && prop.size_chart_id)
    .map((prop) => [prop.property_vendor_id as string, Number(prop.size_chart_id)]);

  const variantSizeCharts = (offer.variant_dimensions ?? [])
    .filter((dimension) => dimension.size_chart_id)
    .map((dimension) => [dimension.property_vendor_id, Number(dimension.size_chart_id)]);

  return {
    selectedGroupId,
    selectedValues: normalizeSelectedValues(selectedCombination?.prop_vendor_ids ?? {}, variantPropertyVendorIds),
    selectedSizeCharts: Object.fromEntries([...primarySizeCharts, ...variantSizeCharts]),
  };
}

export function getFirstCombinationForGroup(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
): MulticardCombination | null {
  if (!groupId) {
    return null;
  }

  return combinations.find((combination) => combination.group_id === groupId) ?? null;
}

export function getFirstInStockCombinationForGroup(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
): MulticardCombination | null {
  if (!groupId) {
    return null;
  }

  return combinations.find((combination) => combination.group_id === groupId && combination.balance > 0) ?? null;
}

export function findBestInStockCombinationForPrimaryGroup(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
  selectedValues: MulticardSelectedValues,
): MulticardCombination | null {
  if (!groupId) {
    return null;
  }

  const selectedEntries = Object.entries(selectedValues).filter(([, value]) => value);
  let bestCombination: MulticardCombination | null = null;
  let bestMatchCount = -1;

  for (const combination of combinations) {
    if (combination.group_id !== groupId || combination.balance < 1) {
      continue;
    }

    const matchCount = selectedEntries.reduce((count, [propertyVendorId, propertyValueVendorId]) => {
      return count + Number(combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId);
    }, 0);

    if (matchCount > bestMatchCount) {
      bestCombination = combination;
      bestMatchCount = matchCount;
    }

    if (matchCount === selectedEntries.length) {
      return combination;
    }
  }

  return bestCombination;
}

export function getPrimaryGroupById(
  primaryGroups: MulticardPrimaryGroup[] = [],
  groupId: number | null,
): MulticardPrimaryGroup | null {
  return primaryGroups.find((group) => group.id === groupId) ?? primaryGroups[0] ?? null;
}

export function findCombinationForSelection(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
  selectedValues: MulticardSelectedValues,
): MulticardCombination | null {
  const selectedEntries = Object.entries(selectedValues).filter(([, value]) => value);

  if (!groupId || selectedEntries.length === 0) {
    return null;
  }

  return (
    combinations.find((combination) => {
      return (
        combination.group_id === groupId &&
        selectedEntries.every(([propertyVendorId, propertyValueVendorId]) => {
          return combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId;
        })
      );
    }) ?? null
  );
}

export function findInStockCombinationForVariantValue(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
  selectedValues: MulticardSelectedValues,
  propertyVendorId: string,
  propertyValueVendorId: string,
): MulticardCombination | null {
  const nextSelectedValues = {
    ...selectedValues,
    [propertyVendorId]: propertyValueVendorId,
  };
  const compatibleCombination = findCombinationForSelection(combinations, groupId, nextSelectedValues);

  if (compatibleCombination && compatibleCombination.balance > 0) {
    return compatibleCombination;
  }

  const sameGroupCombination = combinations.find((combination) => {
    return (
      combination.group_id === groupId &&
      combination.balance > 0 &&
      combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId
    );
  });
  const anyGroupCombination = combinations.find((combination) => {
    return combination.balance > 0 && combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId;
  });

  return sameGroupCombination ?? anyGroupCombination ?? null;
}

export function isVariantValueAvailable(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
  selectedValues: MulticardSelectedValues,
  propertyVendorId: string,
  propertyValueVendorId: string,
): boolean {
  return combinations.some((combination) => {
    if (combination.group_id !== groupId) {
      return false;
    }

    if (combination.prop_vendor_ids[propertyVendorId] !== propertyValueVendorId) {
      return false;
    }

    return Object.entries(selectedValues).every(([selectedPropertyVendorId, selectedPropertyValueVendorId]) => {
      return (
        selectedPropertyVendorId === propertyVendorId ||
        !selectedPropertyValueVendorId ||
        combination.prop_vendor_ids[selectedPropertyVendorId] === selectedPropertyValueVendorId
      );
    });
  });
}

export function isVariantValueInStock(
  combinations: MulticardCombination[] = [],
  groupId: number | null = null,
  propertyVendorId: string,
  propertyValueVendorId: string,
): boolean {
  return combinations.some((combination) => {
    if (groupId !== null && combination.group_id !== groupId) {
      return false;
    }

    return combination.balance > 0 && combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId;
  });
}

export function isVariantValueOutOfStock(
  combinations: MulticardCombination[] = [],
  groupId: number | null,
  propertyVendorId: string,
  propertyValueVendorId: string,
): boolean {
  const matchingCombinations = combinations.filter((combination) => {
    return combination.group_id === groupId && combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId;
  });

  return matchingCombinations.length > 0 && matchingCombinations.every((combination) => combination.balance < 1);
}

export function normalizeSelectedValues(
  values: Record<string, string | null>,
  allowedPropertyVendorIds: string[] | null = null,
): MulticardSelectedValues {
  const allowedPropertyVendorIdMap = allowedPropertyVendorIds ? new Set(allowedPropertyVendorIds) : null;

  return Object.fromEntries(
    Object.entries(values)
      .filter((entry): entry is [string, string] => {
        return Boolean(entry[1]) && (!allowedPropertyVendorIdMap || allowedPropertyVendorIdMap.has(entry[0]));
      })
      .map(([propertyVendorId, propertyValueVendorId]) => [propertyVendorId, propertyValueVendorId]),
  );
}
